import { FormDialog } from '@/components/FormDialog'
import { SelectedEquipmentCard } from '@/components/SelectedEquipmentCard'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { Textarea } from '@/components/ui/textarea'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useGetProducts } from '@/features/equipment/hooks/useProduct'
import { getProductName } from '@/features/equipment/utils/product'
import { useGetConfigs } from '@/features/settings/hooks/useConfig'
import { AppConfigKey } from '@/features/settings/types/config'
import { isApiError } from '@/utils/error'
import { formatBuildingFloorLocation } from '@/utils/location'
import { showError, showSuccess } from '@/utils/toast'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCreateBorrowTransferRequest } from '../hooks/useTransferRequest'

const emptyForm = {
  productId: '',
  quantityRequested: '',
  reason: '',
  targetDepartmentId: '',
}
const selectFieldClassName = 'flex w-full flex-col gap-1.5'
const selectTriggerClassName = 'w-full bg-secondary/50 text-xs'

export function BorrowTransferDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { t } = useTranslation()
  const { departments, refreshMe, isRefreshingMe } = useAuth()
  const { data: configs } = useGetConfigs()
  const [form, setForm] = useState(emptyForm)
  const { data: products = [], isLoading: isLoadingProduct } = useGetProducts(
    {
      excludeDepartmentId: form.targetDepartmentId || undefined,
    },
    open && !!form.targetDepartmentId,
  )
  const createBorrowTransferRequest = useCreateBorrowTransferRequest()
  const selectedProduct = products.find(product => product.id === form.productId)
  const warehouseDepartmentId = configs?.[AppConfigKey.WarehouseDepartmentId]
  const targetDepartments = departments.filter(department => department.id !== warehouseDepartmentId)
  const targetDepartmentOptions = targetDepartments.map(department => ({
    value: department.id,
    label: department.name,
    description: formatBuildingFloorLocation(t, department.buildingName, department.floorName),
  }))
  const productOptions = products.map(product => ({
    value: product.id,
    label: `${getProductName(product.name, product.brand, product.model)} (${product.availableAtOwner}/${product.itemSummary.total})`,
    description: product.categoryName,
    searchValue: `${product.name || ''} ${product.brand || ''} ${product.model || ''} ${product.categoryName || ''}`,
  }))
  const isInvalid = !form.productId || !form.quantityRequested || !form.reason || !form.targetDepartmentId

  useEffect(() => {
    if (!open) {
      setForm(emptyForm)
      return
    }

    let ignore = false
    refreshMe()
      .then(user => {
        if (ignore) return

        const departments = user.departments.filter(department => department.id !== warehouseDepartmentId)
        const targetDepartmentId =
          user.primaryDepartmentId && user.primaryDepartmentId !== warehouseDepartmentId
            ? user.primaryDepartmentId
            : departments[0]?.id || ''

        setForm(current => ({
          ...current,
          targetDepartmentId,
        }))
      })
      .catch(error => {
        if (isApiError(error)) showError(error.message)
      })

    return () => {
      ignore = true
    }
  }, [open, refreshMe, warehouseDepartmentId])

  async function handleSubmit() {
    if (isInvalid) return

    try {
      await createBorrowTransferRequest.mutateAsync({
        productId: form.productId,
        quantityRequested: form.quantityRequested,
        reason: form.reason,
        targetDepartmentId: form.targetDepartmentId,
        imageIds: [],
      })

      setForm(emptyForm)
      showSuccess(t('requests.requestSuccess'))
      onOpenChange(false)
    } catch (error) {
      showError(isApiError(error) ? error.message : t('requests.requestError'))
    }
  }

  return (
    <>
      <FormDialog
        open={open}
        onOpenChange={onOpenChange}
        title={t('requests.borrowTitle')}
        submitLabel={createBorrowTransferRequest.isPending ? t('common.submitting') : t('requests.submitRequest')}
        onSubmit={handleSubmit}
        isInvalid={isInvalid}
        isLoading={isRefreshingMe || createBorrowTransferRequest.isPending}
        contentTestId="borrow-dialog"
        submitTestId="borrow-submit"
      >
        <>
          <div className={selectFieldClassName}>
            <Label className="text-xs text-muted-foreground">{t('requests.requestToDepartment')}</Label>
            <SearchableSelect
              value={form.targetDepartmentId}
              onValueChange={targetDepartmentId => setForm({ ...form, productId: '', targetDepartmentId })}
              options={targetDepartmentOptions}
              placeholder={t('requests.chooseTargetDepartment')}
              searchPlaceholder={t('common.placeholder.searchDepartment')}
              emptyText={isRefreshingMe ? t('common.loading') : t('common.noDepartment')}
              triggerClassName={selectTriggerClassName}
              testId="borrow-target-department"
            />
          </div>
          <div className={selectFieldClassName}>
            <Label className="text-xs">{t('common.placeholder.selectEquipmentModel')} *</Label>
            <SearchableSelect
              value={form.productId}
              onValueChange={productId => setForm({ ...form, productId })}
              options={productOptions}
              placeholder={t('common.placeholder.selectEquipmentModel')}
              searchPlaceholder={t('common.placeholder.searchEquipmentModel')}
              emptyText={
                form.targetDepartmentId
                  ? isLoadingProduct
                    ? t('common.loading')
                    : t('requests.noEquipmentModel')
                  : t('common.placeholder.selectDepartmentFirst')
              }
              disabled={!form.targetDepartmentId}
              triggerClassName={selectTriggerClassName}
              testId="borrow-product"
            />
          </div>
          {selectedProduct && (
            <SelectedEquipmentCard
              name={selectedProduct?.name}
              brand={selectedProduct?.brand}
              model={selectedProduct?.model}
              categoryName={selectedProduct?.categoryName}
            />
          )}
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs">{t('requests.quantity')} *</Label>
            <Input
              data-testid="borrow-quantity"
              type="number"
              min="1"
              value={form.quantityRequested}
              onChange={e => setForm({ ...form, quantityRequested: e.target.value })}
              className="h-9 bg-secondary/50 text-xs"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs">{t('requests.reason')} *</Label>
            <Textarea
              data-testid="borrow-reason"
              value={form.reason}
              onChange={e => setForm({ ...form, reason: e.target.value })}
              className="bg-secondary/50 text-xs"
              rows={2}
              placeholder={t('requests.reasonBorrowPlaceholder')}
            />
          </div>
        </>
      </FormDialog>
    </>
  )
}
